"use client";

// Section animée de la fiche Codex : une tâche confiée de bout en bout.
//
// Ce qui distingue un agent de développement d'un assistant qui répond dans une
// fenêtre, c'est qu'il agit sur le projet : il lit, planifie, modifie, lance les
// tests, puis rend la main. On le fait vivre plutôt que de le raconter : le
// visiteur confie une tâche et voit les étapes arriver une à une, jusqu'à la
// relecture qui lui revient.
//
// Même grammaire que HaSkills (fond sombre, bouton d'action, cartes qui
// apparaissent), en bleu ciel pour distinguer Codex dans le catalogue.
// `useReducedMotion` affiche toutes les étapes d'un coup.

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const STEPS = [
    { icon: "folder-open-bold-duotone", title: "Cx_task_s1_t", detail: "Cx_task_s1_d" },
    { icon: "checklist-minimalistic-bold-duotone", title: "Cx_task_s2_t", detail: "Cx_task_s2_d" },
    { icon: "file-code-bold-duotone", title: "Cx_task_s3_t", detail: "Cx_task_s3_d", diff: true },
    { icon: "test-tube-bold-duotone", title: "Cx_task_s4_t", detail: "Cx_task_s4_d" },
];

// Fichiers touchés, affichés sous l'étape de modification.
const FILES = [
    { name: "app/factures/page.jsx", added: 8, removed: 1 },
    { name: "lib/exportCsv.js", added: 24, removed: 0 },
    { name: "tests/exportCsv.test.js", added: 18, removed: 0 },
];

const STEP_DURATION_MS = 850;

export default function CxTask() {
    const t = useTranslations();
    const reduceMotion = useReducedMotion();

    // Nombre d'étapes affichées, et état de la démonstration.
    const [shown, setShown] = useState(0);
    const [running, setRunning] = useState(false);
    const timer = useRef(null);

    // Un démontage pendant la démonstration laisserait l'intervalle courir.
    useEffect(() => {
        return () => {
            if (timer.current) clearInterval(timer.current);
        };
    }, []);

    const launch = () => {
        if (running) return;

        if (reduceMotion) {
            setShown(STEPS.length);
            return;
        }

        // Le compteur vit hors de l'état : un updater de setState doit rester
        // pur, et le mode strict de React l'appelle deux fois en développement.
        let count = 0;
        setShown(0);
        setRunning(true);
        timer.current = setInterval(() => {
            count += 1;
            setShown(count);
            if (count >= STEPS.length) {
                clearInterval(timer.current);
                timer.current = null;
                setRunning(false);
            }
        }, STEP_DURATION_MS);
    };

    const reset = () => {
        if (timer.current) clearInterval(timer.current);
        timer.current = null;
        setRunning(false);
        setShown(0);
    };

    const isComplete = shown >= STEPS.length;

    return (
        <section id="cx-task" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Cx_task_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Cx_task_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10 p-6 sm:p-10">
                {/* Glows (bleu ciel Codex) */}
                <div className="absolute -top-24 -left-20 w-[420px] h-[420px] bg-sky-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -right-20 w-[380px] h-[380px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="relative">
                    {/* La tâche confiée */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-start gap-3">
                            <div className="w-11 h-11 rounded-xl bg-sky-500/15 ring-1 ring-sky-400/30 flex items-center justify-center shrink-0">
                                <SolarIcon
                                    name="chat-square-code-bold-duotone"
                                    className="w-5 h-5 text-sky-300"
                                />
                            </div>
                            <div>
                                <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-sky-300">
                                    {t("Cx_task_label")}
                                </span>
                                <p className="text-[15px] sm:text-base font-medium text-white leading-snug mb-0 mt-1">
                                    {t("Cx_task_prompt")}
                                </p>
                            </div>
                        </div>

                        <button
                            onClick={isComplete ? reset : launch}
                            disabled={running}
                            className={`shrink-0 rounded-full px-5 py-2.5 text-[13px] font-semibold transition-colors duration-200 ${
                                running
                                    ? "bg-white/[0.06] text-gray-400 ring-1 ring-white/10 cursor-default"
                                    : isComplete
                                      ? "bg-white/[0.08] text-gray-200 ring-1 ring-white/15 hover:bg-white/[0.12]"
                                      : "bg-sky-500 text-white ring-1 ring-sky-400/60 hover:bg-sky-400"
                            }`}
                        >
                            {running
                                ? t("Cx_task_working")
                                : isComplete
                                  ? t("Cx_task_reset")
                                  : t("Cx_task_run")}
                        </button>
                    </div>

                    {/* Étapes */}
                    <div className="mt-6 flex flex-col gap-3">
                        {STEPS.slice(0, shown).map((step) => (
                            <motion.div
                                key={step.title}
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 px-5 py-4"
                            >
                                <div className="flex items-start gap-3">
                                    <SolarIcon
                                        name={step.icon}
                                        className="w-5 h-5 text-sky-300 shrink-0 mt-0.5"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-[15px] font-semibold text-white leading-snug mb-0">
                                            {t(step.title)}
                                        </p>
                                        <p className="text-sm text-gray-300/80 leading-relaxed mb-0 mt-0.5">
                                            {t(step.detail)}
                                        </p>

                                        {step.diff && (
                                            <ul className="mt-3 flex flex-col gap-1.5 rounded-xl bg-black/20 ring-1 ring-white/10 px-3.5 py-3 font-mono text-[12px]">
                                                {FILES.map((file) => (
                                                    <li
                                                        key={file.name}
                                                        className="flex items-center justify-between gap-3"
                                                    >
                                                        <span className="text-gray-200 truncate">
                                                            {file.name}
                                                        </span>
                                                        <span className="shrink-0 tabular-nums">
                                                            <span className="text-emerald-400">
                                                                +{file.added}
                                                            </span>{" "}
                                                            <span className="text-rose-400">
                                                                -{file.removed}
                                                            </span>
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* La main revient au visiteur */}
                    {isComplete && (
                        <motion.div
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            className="mt-5 rounded-2xl bg-sky-500/10 ring-1 ring-sky-400/30 px-5 py-4"
                        >
                            <div className="flex items-start gap-3">
                                <SolarIcon
                                    name="user-check-rounded-bold-duotone"
                                    className="w-5 h-5 text-sky-300 shrink-0 mt-0.5"
                                />
                                <p className="text-[15px] sm:text-base text-gray-100 leading-snug mb-0">
                                    {t.rich("Cx_task_done", {
                                        b: (chunks) => (
                                            <strong className="text-white font-semibold">
                                                {chunks}
                                            </strong>
                                        ),
                                    })}
                                </p>
                            </div>
                        </motion.div>
                    )}
                </div>
            </div>

            <p className="mt-6 text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Cx_task_caption")}
            </p>
        </section>
    );
}
